(() => {
  'use strict';

  const apiKey = '__rdbCollectionCampaignPanels';
  if (window[apiKey]) {
    window[apiKey].initializeAll(document);
    return;
  }

  const historyKey = '__rdbCampaignPanel';
  const sessionId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  const controllers = new WeakMap();
  const historyEntries = new Map();
  const ownedHistoryEvents = new WeakSet();
  let activePanel = null;
  let historyTransition = null;
  let historySerial = 0;
  let scrollLock = null;
  let pendingScrollRestore = null;

  const supportsModalDialog = () => typeof window.HTMLDialogElement !== 'undefined'
    && typeof window.HTMLDialogElement.prototype.showModal === 'function';

  const focusWithoutScroll = (element) => {
    if (!element || !element.isConnected || typeof element.focus !== 'function') return;
    try {
      element.focus({preventScroll: true});
    } catch {
      element.focus();
    }
  };

  const visibleTabbables = (scope) => [...scope.querySelectorAll(
    'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'
  )].filter(element => !element.hidden
    && element.getClientRects().length > 0
    && getComputedStyle(element).visibility !== 'hidden'
    && getComputedStyle(element).display !== 'none');

  const snapshotProperties = (element, properties) => properties.map(property => ({
    property,
    value: element.style.getPropertyValue(property),
    priority: element.style.getPropertyPriority(property),
  }));

  const restoreProperties = (element, snapshot) => snapshot.forEach(({property, value, priority}) => {
    if (value) element.style.setProperty(property, value, priority);
    else element.style.removeProperty(property);
  });

  const lockPageScroll = () => {
    if (scrollLock) return;
    const html = document.documentElement;
    const body = document.body;
    const x = window.scrollX;
    const y = window.scrollY;
    const scrollbarGap = Math.max(0, window.innerWidth - html.clientWidth);
    const bodyProperties = ['position', 'top', 'right', 'left', 'width', 'overflow', 'padding-right'];
    const htmlProperties = ['overflow'];
    scrollLock = {
      x,
      y,
      body,
      html,
      bodySnapshot: snapshotProperties(body, bodyProperties),
      htmlSnapshot: snapshotProperties(html, htmlProperties),
    };
    const computedPadding = Number.parseFloat(getComputedStyle(body).paddingRight) || 0;
    html.style.setProperty('overflow', 'hidden');
    body.style.setProperty('position', 'fixed');
    body.style.setProperty('top', `${-y}px`);
    body.style.setProperty('right', '0');
    body.style.setProperty('left', '0');
    body.style.setProperty('width', '100%');
    body.style.setProperty('overflow', 'hidden');
    if (scrollbarGap) body.style.setProperty('padding-right', `${computedPadding + scrollbarGap}px`);
  };

  const unlockPageScroll = () => {
    if (!scrollLock) return null;
    const current = scrollLock;
    scrollLock = null;
    restoreProperties(current.body, current.bodySnapshot);
    restoreProperties(current.html, current.htmlSnapshot);
    window.scrollTo({left: current.x, top: current.y, behavior: 'instant'});
    return {x: current.x, y: current.y};
  };

  const cancelPendingScrollRestore = () => {
    if (!pendingScrollRestore) return;
    pendingScrollRestore.abortController?.abort();
    if (pendingScrollRestore.frame) cancelAnimationFrame(pendingScrollRestore.frame);
    if (pendingScrollRestore.timeout) clearTimeout(pendingScrollRestore.timeout);
    pendingScrollRestore = null;
  };

  const deferScrollRestore = (position, baseUrl, afterRestore = null) => {
    cancelPendingScrollRestore();
    if (!position || window.location.href !== baseUrl) {
      afterRestore?.();
      return;
    }
    const task = {position, baseUrl, afterRestore, frame: null, timeout: null, abortController: null};
    pendingScrollRestore = task;
    const finish = (restore) => {
      if (pendingScrollRestore !== task) return;
      task.abortController?.abort();
      pendingScrollRestore = null;
      if (restore && !activePanel && window.location.href === task.baseUrl) {
        window.scrollTo({left: task.position.x, top: task.position.y, behavior: 'instant'});
      }
      task.afterRestore?.();
    };
    if (!afterRestore) {
      task.abortController = new AbortController();
      const {signal} = task.abortController;
      const cancelForUser = () => finish(false);
      window.addEventListener('wheel', cancelForUser, {capture: true, passive: true, signal});
      window.addEventListener('touchmove', cancelForUser, {capture: true, passive: true, signal});
      window.addEventListener('pointerdown', cancelForUser, {capture: true, passive: true, signal});
      window.addEventListener('keydown', event => {
        if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(event.key)) cancelForUser();
      }, {capture: true, signal});
    }
    task.frame = requestAnimationFrame(() => {
      if (pendingScrollRestore !== task) return;
      task.frame = requestAnimationFrame(() => {
        if (pendingScrollRestore !== task) return;
        task.timeout = window.setTimeout(() => {
          if (pendingScrollRestore !== task) return;
          if (afterRestore && !activePanel && window.location.href === task.baseUrl) {
            window.scrollTo({left: task.position.x, top: task.position.y, behavior: 'instant'});
            task.frame = requestAnimationFrame(() => finish(false));
            return;
          }
          finish(true);
        }, 0);
      });
    });
  };

  const currentMarker = (state = history.state) => {
    if (!state || typeof state !== 'object' || Array.isArray(state)) return null;
    const marker = state[historyKey];
    return marker && marker.sessionId === sessionId ? marker : null;
  };

  const removeStaleMarker = (state = history.state) => {
    if (!state || typeof state !== 'object' || Array.isArray(state) || !state[historyKey]
      || state[historyKey].sessionId === sessionId) return false;
    const cleanState = {...state};
    delete cleanState[historyKey];
    history.replaceState(cleanState, '', window.location.href);
    return true;
  };

  const stateWithMarker = (state, marker) => {
    const nextState = state && typeof state === 'object' && !Array.isArray(state)
      ? {...state}
      : {};
    nextState[historyKey] = marker;
    return nextState;
  };

  const setExpanded = (panel, expanded) => panel.triggers.forEach(trigger => {
    trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  });

  const closeActivePanel = ({returnFocus = false} = {}) => {
    if (!activePanel) return null;
    const closing = activePanel;
    activePanel = null;
    setExpanded(closing.panel, false);
    if (closing.panel.dialog.open) closing.panel.dialog.close();
    closing.scrollPosition = unlockPageScroll();
    if (returnFocus) focusWithoutScroll(closing.trigger);
    return closing;
  };

  const showPanel = (controller, panel, trigger, historyEntry = null) => {
    if (!panel.dialog.isConnected || panel.dialog.open || activePanel) return false;
    cancelPendingScrollRestore();
    let entry = historyEntry;
    let pushedEntry = false;
    if (!entry) {
      historyEntries.clear();
      const token = `${sessionId}-${(historySerial += 1).toString(36)}`;
      const triggerIndex = Math.max(0, panel.triggers.indexOf(trigger));
      const marker = {sessionId, token, dialogId: panel.dialog.id, triggerIndex};
      entry = {
        marker,
        controller,
        panel,
        trigger,
        baseState: history.state,
        baseUrl: window.location.href,
      };
      try {
        history.pushState(stateWithMarker(entry.baseState, marker), '', entry.baseUrl);
      } catch {
        return false;
      }
      historyEntries.set(token, entry);
      pushedEntry = true;
    }

    lockPageScroll();
    try {
      panel.dialog.showModal();
    } catch {
      unlockPageScroll();
      if (pushedEntry && currentMarker()?.token === entry.marker.token) {
        historyEntries.delete(entry.marker.token);
        historyTransition = {type: 'failed-open', pendingOpen: null};
        history.back();
      }
      return false;
    }

    activePanel = {...entry, controller, panel, trigger};
    setExpanded(panel, true);
    focusWithoutScroll(panel.heading || panel.closeButton);
    return true;
  };

  const requestOpen = (controller, panel, trigger) => {
    if (historyTransition) {
      if (historyTransition.type === 'close') historyTransition.pendingOpen = {controller, panel, trigger};
      return;
    }
    if (activePanel) return;
    showPanel(controller, panel, trigger);
  };

  const requestClose = () => {
    if (!activePanel) return;
    const closing = activePanel;
    const ownsCurrentEntry = currentMarker()?.token === closing.marker.token;
    closeActivePanel({returnFocus: true});
    if (!ownsCurrentEntry) return;
    historyTransition = {
      type: 'close',
      pendingOpen: null,
      scrollPosition: closing.scrollPosition,
      baseUrl: closing.baseUrl,
    };
    history.back();
  };

  const requestProductNavigation = (href) => {
    if (!activePanel) {
      window.location.assign(href);
      return;
    }
    const closing = activePanel;
    const ownsCurrentEntry = currentMarker()?.token === closing.marker.token;
    closeActivePanel({returnFocus: false});
    if (!ownsCurrentEntry) {
      window.location.assign(href);
      return;
    }

    history.replaceState(closing.baseState, '', closing.baseUrl);
    historyTransition = {
      type: 'navigate',
      href,
      scrollPosition: closing.scrollPosition,
      baseUrl: closing.baseUrl,
    };
    history.back();
  };

  const ownsHistoryEvent = (event) => {
    if (!event || (typeof event !== 'object' && typeof event !== 'function')) return false;
    if (ownedHistoryEvents.has(event)) return true;
    const activePanelOwnsEvent = activePanel && window.location.href === activePanel.baseUrl;
    const ownsEvent = Boolean(currentMarker(event.state) || historyTransition || activePanelOwnsEvent);
    if (ownsEvent) ownedHistoryEvents.add(event);
    return ownsEvent;
  };

  const handlePopState = (event) => {
    // Preserve the ownership decision for catalogue routers registered before or after this handler.
    const ownsEvent = ownsHistoryEvent(event);
    removeStaleMarker(event.state);
    const marker = currentMarker(event.state);
    if (marker) {
      historyTransition = null;
      if (activePanel?.marker.token === marker.token) return;
      if (activePanel) closeActivePanel({returnFocus: false});
      const entry = historyEntries.get(marker.token);
      if (!entry || !entry.panel.dialog.isConnected) return;
      const trigger = entry.panel.triggers[marker.triggerIndex] || entry.trigger;
      showPanel(entry.controller, entry.panel, trigger, entry);
      return;
    }

    const closing = activePanel ? closeActivePanel({returnFocus: true}) : null;
    const transition = historyTransition;
    historyTransition = null;
    const position = transition?.scrollPosition || closing?.scrollPosition;
    const baseUrl = transition?.baseUrl || closing?.baseUrl;
    if (!transition) {
      if (ownsEvent && position && window.location.href === baseUrl) deferScrollRestore(position, baseUrl);
      return;
    }
    if (transition.type === 'navigate') {
      if (position && window.location.href === baseUrl) {
        deferScrollRestore(position, baseUrl, () => window.location.assign(transition.href));
      } else window.location.assign(transition.href);
      return;
    }
    if (transition.pendingOpen) {
      const {controller, panel, trigger} = transition.pendingOpen;
      requestOpen(controller, panel, trigger);
      return;
    }
    if (ownsEvent && position && window.location.href === baseUrl) deferScrollRestore(position, baseUrl);
  };

  const createRailController = (root, signal) => {
    const rail = root.querySelector('[data-collection-campaign-rail]');
    const controls = root.querySelector('[data-collection-campaign-controls]');
    const previous = root.querySelector('[data-collection-campaign-previous]');
    const next = root.querySelector('[data-collection-campaign-next]');
    const dots = root.querySelector('[data-collection-campaign-dots]');
    if (!rail || !controls || !previous || !next || !dots) return null;

    const scrollBehavior = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth';
    const cards = [...rail.querySelectorAll('.rdb-collection-campaign__item')];
    const rtl = root.hasAttribute('data-indexed-ads') && getComputedStyle(rail).direction === 'rtl';
    const logicalScroll = () => rtl ? -rail.scrollLeft : rail.scrollLeft;
    const positions = () => cards.map(card => Math.abs(card.offsetLeft - cards[0].offsetLeft));
    let active = 0;
    let scheduled = false;
    let animationFrame = null;
    const move = (index) => {
      const max = Math.max(0, rail.scrollWidth - rail.clientWidth);
      const target = Math.min(cards.length - 1, Math.max(0, index));
      rail.scrollTo({left: Math.min(positions()[target], max) * (rtl ? -1 : 1), behavior: scrollBehavior()});
    };
    const buttons = cards.map((card, index) => {
      const button = document.createElement('button');
      const heading = card.querySelector('h2');
      const title = heading ? (heading.innerText || heading.textContent).replace(/\s+/g, ' ').trim() : '';
      button.type = 'button';
      button.dataset.collectionCampaignDot = String(index);
      button.setAttribute('aria-controls', rail.id);
      button.setAttribute('aria-label', dots.dataset.label.replace('__TITLE__', title));
      button.addEventListener('click', () => move(index), {signal});
      dots.append(button);
      return button;
    });
    signal.addEventListener('abort', () => {
      if (animationFrame !== null) cancelAnimationFrame(animationFrame);
      buttons.forEach(button => button.remove());
    }, {once: true});
    const update = () => {
      const max = Math.max(0, rail.scrollWidth - rail.clientWidth);
      const overflow = max >= 8 && cards.length > 1;
      controls.hidden = !overflow;
      if (overflow) rail.tabIndex = 0;
      else rail.removeAttribute('tabindex');
      const positionsNow = positions();
      active = positionsNow.reduce((best, position, index) => Math.abs(Math.min(position, max) - logicalScroll()) < Math.abs(Math.min(positionsNow[best], max) - logicalScroll()) ? index : best, 0);
      if (overflow && logicalScroll() >= max - 8) active = cards.length - 1;
      previous.disabled = logicalScroll() < 8;
      next.disabled = logicalScroll() >= max - 8;
      buttons.forEach((button, index) => button.setAttribute('aria-current', String(index === active)));
    };
    const onKeydown = (event) => {
      if (event.target !== rail) return;
      if (event.key === 'ArrowLeft') { event.preventDefault(); move(active + (rtl ? 1 : -1)); }
      if (event.key === 'ArrowRight') { event.preventDefault(); move(active + (rtl ? -1 : 1)); }
      if (event.key === 'Home') { event.preventDefault(); move(0); }
      if (event.key === 'End') { event.preventDefault(); move(cards.length - 1); }
    };

    previous.addEventListener('click', () => move(active - 1), {signal});
    next.addEventListener('click', () => move(active + 1), {signal});
    rail.addEventListener('keydown', onKeydown, {signal});
    rail.addEventListener('scroll', () => {
      if (!scheduled) {
        scheduled = true;
        animationFrame = requestAnimationFrame(() => {
          animationFrame = null;
          scheduled = false;
          if (!signal.aborted) update();
        });
      }
    }, {passive: true, signal});
    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(update);
      resizeObserver.observe(rail);
      signal.addEventListener('abort', () => resizeObserver.disconnect(), {once: true});
    } else window.addEventListener('resize', update, {passive: true, signal});
    update();
    return {rail, controls, previous, next, dots, update};
  };

  const buildPanels = (root) => {
    if (!supportsModalDialog()) return null;
    const dialogs = [...root.querySelectorAll('dialog[data-campaign-panel]')];
    const triggers = [...root.querySelectorAll('button[data-campaign-open][aria-controls]')];
    const fallbacks = [...root.querySelectorAll('details[data-campaign-fallback]')];
    if (!dialogs.length || !triggers.length || fallbacks.length !== dialogs.length) return null;

    const panelById = new Map();
    for (const dialog of dialogs) {
      const content = dialog.querySelector('[data-campaign-panel-content]');
      const heading = dialog.querySelector('[data-campaign-panel-heading]');
      const closeButton = dialog.querySelector('button[data-campaign-close]');
      const item = dialog.closest('.rdb-collection-campaign__item');
      const fallback = item?.querySelector('details[data-campaign-fallback]');
      const uniqueId = dialog.id && [...document.querySelectorAll('[id]')].filter(element => element.id === dialog.id).length === 1;
      if (!uniqueId || dialog.open || panelById.has(dialog.id)
        || !content || !heading?.id || heading.tabIndex !== -1 || !closeButton || !fallback
        || !content.contains(heading) || !content.contains(closeButton)
        || dialog.getAttribute('aria-labelledby') !== heading.id) return null;
      panelById.set(dialog.id, {dialog, content, heading, closeButton, fallback, item, triggers: []});
    }

    for (const trigger of triggers) {
      const panel = panelById.get(trigger.getAttribute('aria-controls'));
      if (!panel || !trigger.hasAttribute('hidden') || trigger.type !== 'button'
        || trigger.closest('.rdb-collection-campaign__item') !== panel.item) return null;
      panel.triggers.push(trigger);
    }
    if ([...panelById.values()].some(panel => panel.triggers.length !== (root.hasAttribute('data-indexed-ads') ? 1 : 2) || panel.fallback.hidden)) return null;
    return [...panelById.values()];
  };

  const initialize = (root) => {
    if (!(root instanceof Element) || controllers.has(root)) return;
    const abortController = new AbortController();
    const {signal} = abortController;
    const readyBefore = root.getAttribute('data-collection-campaign-ready');
    const panelReadyBefore = root.getAttribute('data-campaign-panel-ready');
    const railController = createRailController(root, signal);
    if (!railController) {
      abortController.abort();
      return;
    }

    const panels = buildPanels(root);
    const triggerState = new Map();
    const fallbackState = new Map();
    const controller = {
      root,
      railController,
      panels: panels || [],
      abortController,
      readyBefore,
      panelReadyBefore,
      triggerState,
      fallbackState,
      destroy: null,
    };

    if (panels) {
      panels.forEach(panel => {
        panel.triggers.forEach(trigger => {
          triggerState.set(trigger, {
            hidden: trigger.hidden,
            ariaExpanded: trigger.getAttribute('aria-expanded'),
          });
          trigger.addEventListener('click', () => requestOpen(controller, panel, trigger), {signal});
        });
        fallbackState.set(panel.fallback, panel.fallback.hidden);
        panel.closeButton.addEventListener('click', requestClose, {signal});
        panel.dialog.addEventListener('cancel', (event) => {
          event.preventDefault();
          requestClose();
        }, {signal});
        panel.dialog.addEventListener('keydown', (event) => {
          if (event.key !== 'Tab' || event.defaultPrevented) return;
          const tabbables = visibleTabbables(panel.dialog);
          if (!tabbables.length) {
            event.preventDefault();
            focusWithoutScroll(panel.heading);
            return;
          }
          const first = tabbables[0];
          const last = tabbables[tabbables.length - 1];
          const active = document.activeElement;
          if (active === panel.heading) {
            event.preventDefault();
            focusWithoutScroll(event.shiftKey ? last : first);
          } else if (event.shiftKey && active === first) {
            event.preventDefault();
            focusWithoutScroll(last);
          } else if (!event.shiftKey && active === last) {
            event.preventDefault();
            focusWithoutScroll(first);
          }
        }, {signal});
        panel.dialog.addEventListener('close', () => {
          if (activePanel?.panel === panel) requestClose();
        }, {signal});

        let backdropPointer = null;
        panel.dialog.addEventListener('pointerdown', (event) => {
          backdropPointer = !panel.content.contains(event.target) ? event.pointerId : null;
        }, {signal});
        panel.dialog.addEventListener('pointerup', (event) => {
          const shouldClose = backdropPointer === event.pointerId && !panel.content.contains(event.target);
          backdropPointer = null;
          if (shouldClose) requestClose();
        }, {signal});
        panel.dialog.addEventListener('pointercancel', () => { backdropPointer = null; }, {signal});
        panel.dialog.addEventListener('click', (event) => {
          const link = event.target.closest('a[href]');
          if (!link || !panel.dialog.contains(link) || event.defaultPrevented || event.button !== 0
            || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey
            || link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
          event.preventDefault();
          requestProductNavigation(link.href);
        }, {signal});
      });

      panels.forEach(panel => {
        panel.triggers.forEach(trigger => {
          trigger.hidden = false;
          trigger.setAttribute('aria-expanded', 'false');
        });
        panel.fallback.hidden = true;
      });
      root.dataset.campaignPanelReady = 'true';
    }

    root.dataset.collectionCampaignReady = 'true';
    controller.destroy = () => {
      if (activePanel?.controller === controller) {
        const closing = activePanel;
        const ownsCurrentEntry = currentMarker()?.token === closing.marker.token;
        closeActivePanel({returnFocus: false});
        if (ownsCurrentEntry) history.replaceState(closing.baseState, '', closing.baseUrl);
      }
      abortController.abort();
      triggerState.forEach((state, trigger) => {
        trigger.hidden = state.hidden;
        if (state.ariaExpanded === null) trigger.removeAttribute('aria-expanded');
        else trigger.setAttribute('aria-expanded', state.ariaExpanded);
      });
      fallbackState.forEach((hidden, fallback) => { fallback.hidden = hidden; });
      historyEntries.forEach((entry, token) => {
        if (entry.controller === controller) historyEntries.delete(token);
      });
      if (readyBefore === null) root.removeAttribute('data-collection-campaign-ready');
      else root.setAttribute('data-collection-campaign-ready', readyBefore);
      if (panelReadyBefore === null) root.removeAttribute('data-campaign-panel-ready');
      else root.setAttribute('data-campaign-panel-ready', panelReadyBefore);
      controllers.delete(root);
    };
    controllers.set(root, controller);
  };

  const rootsWithin = (scope) => {
    const roots = [];
    if (scope instanceof Element && scope.matches('[data-collection-campaign]')) roots.push(scope);
    scope.querySelectorAll?.('[data-collection-campaign]').forEach(root => roots.push(root));
    return roots;
  };

  const initializeAll = (scope = document) => rootsWithin(scope).forEach(initialize);
  const destroyAll = (scope) => rootsWithin(scope).forEach(root => controllers.get(root)?.destroy());

  let rootObserver = null;
  const observeCampaignRoots = () => {
    if (rootObserver || !document.body) return;
    rootObserver = new MutationObserver(records => {
      const removedRoots = new Set();
      const addedRoots = new Set();
      records.forEach(record => {
        record.removedNodes.forEach(node => {
          if (node instanceof Element) rootsWithin(node).forEach(root => removedRoots.add(root));
        });
        record.addedNodes.forEach(node => {
          if (node instanceof Element) rootsWithin(node).forEach(root => addedRoots.add(root));
        });
      });
      removedRoots.forEach(root => controllers.get(root)?.destroy());
      addedRoots.forEach(initialize);
    });
    rootObserver.observe(document.body, {childList: true, subtree: true});
  };

  const start = () => {
    initializeAll(document);
    observeCampaignRoots();
  };

  const api = {initializeAll, destroyAll, ownsHistoryEvent};
  window[apiKey] = api;
  removeStaleMarker();
  window.addEventListener('popstate', handlePopState, true);
  document.addEventListener('shopify:section:load', event => initializeAll(event.target));
  document.addEventListener('shopify:section:unload', event => destroyAll(event.target));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, {once: true});
  else start();
})();
